import React from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { formatRainMm, parseDateInputLocal } from "../../utils/rainHeatmapUtils";

function formatMonthTick(monthId, locale) {
  const match = String(monthId || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return monthId;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  const name = date
    .toLocaleDateString(locale, { month: "short" })
    .replace(".", "");
  return `${name}/${String(match[1]).slice(2)}`;
}

function YearAxisTick({ x, y, payload, locale, mmById }) {
  const mm = mmById[payload.value];
  const hasRain = Number(mm) > 0;
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={12} textAnchor="middle" fontSize={11} fill="#607d8b">
        {formatMonthTick(payload.value, locale)}
      </text>
      <text
        dy={26}
        textAnchor="middle"
        fontSize={10}
        fontWeight={hasRain ? 700 : 500}
        fill={hasRain ? "#1565C0" : "#90A4AE"}
      >
        {formatRainMm(mm)} mm
      </text>
    </g>
  );
}

function RainYearTooltip({ active, payload, locale }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const date = parseDateInputLocal(row.start);
  const title = date
    ? date.toLocaleDateString(locale, { month: "long", year: "numeric" })
    : row.id;
  return (
    <div className="rain-month-chart__tooltip">
      <strong>{title}</strong>
      <span>{formatRainMm(row.mm)} mm</span>
    </div>
  );
}

function RainYearChart({ series, selectedMonthId, isExample = false, onMonthClick }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "en-US" ? "en-US" : "pt-BR";
  const data = series || [];
  const mmById = {};
  data.forEach((row) => {
    mmById[row.id] = row.mm;
  });

  function handleClick(state) {
    const payload =
      state && state.activePayload && state.activePayload[0]
        ? state.activePayload[0].payload
        : null;
    if (!payload || !onMonthClick) return;
    onMonthClick(payload);
  }

  return (
    <section
      className={
        isExample
          ? "rain-month-chart rain-year-chart rain-month-chart--example"
          : "rain-month-chart rain-year-chart"
      }
      aria-label={t("Chuva do ano")}
    >
      <div className="rain-month-chart__head">
        <div>
          <strong>{t("Chuva do ano")}</strong>
          <span>{t("Últimos 12 meses")}</span>
        </div>
      </div>
      <p className="rain-month-chart__hint">
        {isExample
          ? t("Exemplo visual dos 12 meses. Clique num mês para abrir os dias abaixo.")
          : t("Clique no mês para abrir o gráfico de dias correspondente.")}
      </p>
      <div className="rain-year-chart__plot">
        <ResponsiveContainer width="100%" height={176}>
          <BarChart
            data={data}
            margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
            onClick={handleClick}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="id"
              interval={0}
              height={40}
              tick={<YearAxisTick locale={locale} mmById={mmById} />}
            />
            <YAxis
              width={36}
              tick={{ fontSize: 11, fill: "#607d8b" }}
              tickFormatter={(value) => formatRainMm(value)}
            />
            <Tooltip
              cursor={{ fill: "rgba(21, 101, 192, 0.08)" }}
              content={<RainYearTooltip locale={locale} />}
            />
            <Bar dataKey="mm" maxBarSize={36} radius={[3, 3, 0, 0]}>
              {data.map((row) => {
                const selected = row.id === selectedMonthId;
                return (
                  <Cell
                    key={row.id}
                    fill={
                      selected ? "#0D47A1" : row.mm > 0 ? "#42A5F5" : "#CFD8DC"
                    }
                    cursor="pointer"
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

export default RainYearChart;
